import React, { useEffect, useRef, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { Camera } from "@mediapipe/camera_utils";
import { AspectRatio } from "@mui/icons-material";
import { Text, Button, Icon, Accordion, AccordionPanel, Container } from "@jds/core";
import { Box, LinearProgress, Stack } from "@mui/material";
import JdsModal from "../JdsModal";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const styles = {
  circleWrapper: { maxWidth: 320, width: '100%', aspectRatio: 1, margin: "0 auto", borderRadius: "50%", overflow: "hidden", position: "relative", background: "#000" },
  video: { width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" },
  canvas: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" },
  previewImg: { maxWidth: 280, width: '100%', aspectRatio: 1, borderRadius: "50%", objectFit: "cover" },
  progressOuter: { width: "90%", height: 6, background: "#ddd", margin: "10px auto", borderRadius: 10 },
  progressInner: { height: "100%", background: "#28a745", borderRadius: 10, transition: '.2s' },
  buttons: { marginTop: 20, display: "flex", justifyContent: "center", gap: 10 },
  button: { padding: "12px 20px", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer" },
  closeBtn: { padding: "10px 18px", background: "#666", color: "#fff", borderRadius: 10, border: "none", cursor: "pointer" },
};



export default function ImageCam({ onCapture, onClose, isGetFaceCounting, setCamKey = () => { }, ...props }) {
  const loginDetails = useSelector((state) => state.auth.loginDetails);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const steps = [
    "Nod your head",
    "Blink your eyes"
    // "Look straight",
    // "Turn head left",
    //  "Turn head right",
  ];

  // const stepCount = steps.length;
  const stepCount = 2;

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [faceMissing, setFaceMissing] = useState(false);
  const [isFaceInside, setIsFaceInside] = useState(true);
  const [isCapturing, setIsCapturing] = useState(false);
  const [faceDistance, setFaceDistance] = useState("ok");
  const [skipConfirmationModalClose, setSkipConfirmationModalClose] = useState(true)


  const camRef = useRef(null);
  const faceMeshRef = useRef(null);

  const lastSeenRef = useRef(Date.now());
  const cooldownUntilRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const currentStepRef = useRef(0);
  const stepText = useRef(0)
  const faceLostCounter = useRef(0);
  const faceFoundCounter = useRef(0);
  const outsideCounter = useRef(0);
  const detectionLockedRef = useRef(false);
  const OUTSIDE_FRAMES_REQUIRED = 5;



  const SMOOTH = 6;
  function push(hist, v, max = SMOOTH) {
    hist.push(v);
    if (hist.length > max) hist.shift();
  }
  function avg(hist) {
    if (hist.length === 0) return null;
    return hist.reduce((s, v) => s + v, 0) / hist.length;
  }

  const noseXHist = useRef([]);
  const noseYHist = useRef([]);
  const leftOpenHist = useRef([]);
  const rightOpenHist = useRef([]);

  const counters = useRef({ center: 0, left: 0, right: 0, nod: 0, blink: 0 });

  const startCamera = async () => {
    const fm = new FaceMesh({ locateFile: (f) => `/mediapipe/face_mesh/${f}` });
    fm.setOptions({ maxNumFaces: 1, refineLandmarks: true, minDetectionConfidence: 0.6, minTrackingConfidence: 0.6 });
    fm.onResults(onResults);
    faceMeshRef.current = fm;
    const cam = new Camera(videoRef.current, { width: 640, height: 640, onFrame: async () => { try { await fm.send({ image: videoRef.current }); } catch { } } });
    cam.start().catch(() => { });
    camRef.current = cam;
  }

  function resetStats() {
    noseXHist.current = [];
    noseYHist.current = [];
    leftOpenHist.current = [];
    rightOpenHist.current = [];
    counters.current = { center: 0, left: 0, right: 0, nod: 0, blink: 0 };
  }

  function draw(img) {
    const c = canvasRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    c.width = 640;
    c.height = 640;
    ctx.save();
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.scale(-1, 1);
    ctx.translate(-c.width, 0);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    ctx.restore();
  }

  function onResults(res) {
    // 🚫 STOP ALL DETECTION AFTER CAPTURE
    if (detectionLockedRef.current) return;

    const faces = res?.multiFaceLandmarks;
    const now = Date.now();

    // Always update lastSeenRef if we got an image (keep camera running)
    if (res?.image) draw(res.image);

    // If no faces -> run watchdog by calling detectStep(null)
    // (this avoids an early return that prevents face-lost resets)
    if (!faces || faces.length === 0) {
      // mark UI quickly after a little delay to avoid flicker
      if (now - lastSeenRef.current > 300) setFaceMissing(true);

      // run detection/watchdog with null landmarks so detectStep can handle "face lost" counting
      // don't await; keep the pipeline fast
      try {
        detectStep(null);
      } catch (e) {
        // swallow errors to keep the camera loop alive
        console.warn("detectStep(null) error:", e);
      }

      // do not return here — we've already handled the "no face" case above
      return;
    }

    // Face(s) present:
    lastSeenRef.current = now;
    if (faceMissing) setFaceMissing(false);

    // call detectStep with the first face's landmarks
    if (!isTransitioningRef.current && now >= cooldownUntilRef.current) {
      try {
        detectStep(faces[0]);
      } catch (e) {
        console.warn("detectStep error:", e);
      }
    }
  }

  function passStep() {
    if (isTransitioningRef.current) return;
    isTransitioningRef.current = true;
    cooldownUntilRef.current = Date.now() + 1500;
    setCurrentStep((prev) => {
      const next = Math.min(prev + 1, stepCount);

      currentStepRef.current = next;   // 🔥 IMPORTANT FIX

      setProgress(Math.round((next / stepCount) * 100));
      return next;
    });

    resetStats();
    setTimeout(() => { isTransitioningRef.current = false; }, 1500);
  }

  function isFaceInsideCircle(lm) {
    if (!lm || lm.length === 0) return false;

    // MediaPipe nose tip index (FaceMesh)
    const NOSE_TIP_INDEX = 1;

    const nose = lm[NOSE_TIP_INDEX];

    const video = videoRef.current;
    if (!video) return false;

    const width = video.videoWidth;
    const height = video.videoHeight;

    const centerX = width / 2;
    const centerY = height / 2;

    const radius = Math.min(width, height) * 0.30; // adjust as needed

    const noseX = nose.x * width;
    const noseY = nose.y * height;

    const dx = noseX - centerX;
    const dy = noseY - centerY;

    return (dx * dx + dy * dy) < (radius * radius);

  }

  function checkFaceDistance(lm) {
    if (!lm) return "far";

    const leftCheek = lm[234];
    const rightCheek = lm[454];

    if (!leftCheek || !rightCheek) return "far";

    const faceWidth = Math.abs(rightCheek.x - leftCheek.x);

    // Tune these based on testing
    if (faceWidth > 0.38) {
      return "close";   // too close
    }

    if (faceWidth < 0.30) {
      return "far";     // too far
    }

    return "ok";
  }

  function detectStep(lm) {
    if (detectionLockedRef.current) return;

    // ===============================
    // FACE LOST WATCHDOG (CLEAN VERSION)
    // ===============================
    const FACE_LOST_FRAMES = 8;   // How many frames of no landmark before reset
    const FACE_FOUND_FRAMES = 3;  // Required frames to confirm face is back

    // CASE 1 → No face landmarks
    if (!lm || lm.length === 0) {

      faceLostCounter.current++;
      faceFoundCounter.current = 0;

      if (faceLostCounter.current >= FACE_LOST_FRAMES) {
        // console.log("⚠️ FACE LOST — resetting steps to prevent spoofing.");

        // Reset stats
        resetStats();

        // Reset step ref + UI state (both)
        currentStepRef.current = 0;
        setCurrentStep(0);
        setProgress(0);

        // Show UI message
        setFaceMissing(true);
      }

      return; // skip detection until face returns
    }


    // CASE 2 → Face detected again
    faceLostCounter.current = 0;
    faceFoundCounter.current++;
    setFaceMissing(false);

    // ----------------------------
    // FACE DISTANCE CHECK
    // ----------------------------
    const distance = checkFaceDistance(lm);

    if (distance !== "ok") {
      setFaceDistance(distance);
      setIsFaceInside(false)

      resetStats();
      currentStepRef.current = 0;
      setCurrentStep(0);
      setProgress(0);

      return; // stop detection
    } else {
      setFaceDistance("ok");
    }

    // Hide "face not detected" after enough consistent frames
    if (faceFoundCounter.current >= FACE_FOUND_FRAMES) {
      if (faceMissing) setFaceMissing(false);
    }

    // ===============================
    // 🔥 FACE OUTSIDE CIRCLE CHECK
    // ===============================
    if (!isFaceInsideCircle(lm)) {

      setIsFaceInside(false);   // 🔴 update UI

      outsideCounter.current++;

      if (outsideCounter.current >= OUTSIDE_FRAMES_REQUIRED) {
        resetStats();
        currentStepRef.current = 0;
        setCurrentStep(0);
        setProgress(0);

        // 🧹 Clear smoothing history
        noseXHist.current = [];
        noseYHist.current = [];
        leftOpenHist.current = [];
        rightOpenHist.current = [];
      }

      return; // ⛔ stop step detection
    } else {
      outsideCounter.current = 0;
      setIsFaceInside(true);   // 🟢 update UI
    }


    if (currentStepRef.current === steps.length) return; // Stop detection when all the steps are passed.
    const step = currentStepRef.current;

    const nose = lm[1];
    const leftEye = lm[33];
    const rightEye = lm[263];
    const leftCheek = lm[234];
    const rightCheek = lm[454];


    if (!nose || !leftEye || !rightEye || !leftCheek || !rightCheek) return;
    const midX = (leftEye.x + rightEye.x) / 2;
    const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
    if (faceWidth < 0.01) return;
    push(noseXHist.current, nose.x);
    const smNoseX = avg(noseXHist.current);
    if (smNoseX === null) return;
    const rawOffset = smNoseX - midX;
    const noseOffset = rawOffset / faceWidth;
    const leftTop = lm[159];
    const leftBottom = lm[145];
    const rightTop = lm[386];
    const rightBottom = lm[374];
    push(noseYHist.current, nose.y);
    // console.log("noseOffset:", noseOffset.toFixed(4), "step:", currentStep);

    // console.log(
    //   "%c[DEBUG] step=" + step +
    //   " | noseOffset=" + noseOffset.toFixed(4) +
    //   " | raw=" + rawOffset.toFixed(4) +
    //   " | faceWidth=" + faceWidth.toFixed(4) +
    //   " | counters=" + counters.current.center,
    //   "color: #4CAF50; font-weight: bold;"
    // );


    const CENTER_T = 0.12;
    const TURN_T = 0.12;
    const DEPTH_T = 0.05;
    const NOD_T = 0.025;


    // console.log("[STEP]" + step);

    // ---------------------------------------------------------
    // STEP 0 — LOOK STRAIGHT (FULLY REWRITTEN)
    // ---------------------------------------------------------
    // if (step === 0) {
    //   console.log("[STEP 0] Checking LOOK STRAIGHT");

    //   // Wait until smoothing history is ready
    //   if (noseXHist.current.length < SMOOTH) return;

    //   const offsetAbs = Math.abs(noseOffset);

    //   // NEW tuned thresholds:
    //   const STRAIGHT_ACCEPT = 0.045;     // must stay inside ±4.5%
    //   const STRAIGHT_FORGIVE = 0.08;     // noise zone — ignore
    //   const STRAIGHT_RESET = 0.12;       // strong deviation resets counter
    //   const REQUIRED_STABLE_FRAMES = 5;  // must hold for 5 consecutive frames


    //   if (offsetAbs < STRAIGHT_ACCEPT) {
    //     counters.current.center++;
    //     console.log("[STEP 0] +1 center_frame →", counters.current.center);
    //   }


    //   else if (offsetAbs > STRAIGHT_RESET) {
    //     console.log("[STEP 0] ❌ RESET — Too far:", offsetAbs.toFixed(4));
    //     counters.current.center = 0;
    //   }

    //   // 3️⃣ Weak deviation/noise → do nothing (no increment/reset)
    //   // (between ACCEPT and FORGIVE range)

    //   // 4️⃣ Complete step when stable for required frames
    //   if (counters.current.center >= REQUIRED_STABLE_FRAMES) {
    //     console.log("[STEP 0] ✅ PASSED LOOK STRAIGHT");
    //     passStep();
    //   }

    //   return;
    // }


    // ---------------------------------------------------------
    // STEP 1 — TURN LEFT  (with MIRROR FIX)
    // ---------------------------------------------------------
    // if (step === 1) {
    //   const offset = -noseOffset;   // IMPORTANT FIX
    //   console.log("[STEP 1] Checking TURN LEFT | offset=", offset.toFixed(4));

    //   const LEFT_ACCEPT = -0.35;
    //   const LEFT_RESET = -0.10;
    //   const REQUIRED_FRAMES = 4;

    //   if (offset < LEFT_ACCEPT) {
    //     counters.current.left++;
    //     console.log("[STEP 1] +1 left_frame →", counters.current.left);
    //   }
    //   else if (offset > LEFT_RESET) {
    //     counters.current.left = 0;
    //     console.log("[STEP 1] ❌ RESET — centered");
    //   }

    //   if (counters.current.left >= REQUIRED_FRAMES) {
    //     console.log("[STEP 1] ✅ PASSED TURN LEFT");
    //     passStep();
    //   }

    //   return;
    // }


    // ---------------------------------------------------------
    // STEP 2 — TURN RIGHT (tuned to your actual noseOffset)
    // ---------------------------------------------------------
    // if (step === 2) {
    //   const offset = -noseOffset;  // positive for right turn

    //   console.log("[STEP 2] Checking TURN RIGHT | offset=", offset);

    //   // Real tuned thresholds
    //   const RIGHT_ACCEPT = 0.3;   // pass when turning enough (10%)
    //   const RIGHT_RESET = 0.03;    // too centered (3%)
    //   const REQUIRED_FRAMES = 4;

    //   // Enough right turn
    //   if (offset > RIGHT_ACCEPT) {
    //     counters.current.right++;
    //     console.log("[STEP 2] +1 right_frame =", counters.current.right);
    //   }

    //   // Too centered → reset
    //   else if (offset < RIGHT_RESET) {
    //     counters.current.right = 0;
    //     console.log("[STEP 2] ❌ RESET — too centered");
    //   }

    //   // Pass
    //   if (counters.current.right >= REQUIRED_FRAMES) {
    //     console.log("[STEP 2] ✅ PASSED TURN RIGHT");
    //     passStep();
    //   }

    //   return;
    // }



    // ---------------------------------------------------------
    // STEP 3 — NOD HEAD DOWN (FULLY REWRITTEN LIKE STEPS 0–2)
    // ---------------------------------------------------------
    if (step === 0) {
      // console.log("[STEP 3] Checking NOD DOWN");

      // Need enough history to measure vertical change
      if (noseYHist.current.length < SMOOTH) return;

      // Compare oldest vs newest smoothed values
      const first = noseYHist.current[0];
      const last = noseYHist.current[noseYHist.current.length - 1];

      // Positive delta → head moves DOWN
      const deltaY = last - first;

      const NOD_ACCEPT = 0.030;      // must move down at least 2%
      const NOD_RESET = 0.005;       // tiny/no movement → reset
      const REQUIRED_FRAMES = 4;

      // console.log(
      //   `[STEP 3] deltaY=${deltaY.toFixed(4)} | counter=${counters.current.nod}`
      // );

      // Enough downward movement → count frames
      if (deltaY > NOD_ACCEPT) {
        counters.current.nod++;
        // console.log("[STEP 3] +1 nod_frame →", counters.current.nod);
      }

      // No meaningful movement? reset
      else if (deltaY < NOD_RESET) {
        counters.current.nod = 0;
        // console.log("[STEP 3] ❌ RESET — no nod");
      }

      // Passed
      if (counters.current.nod >= REQUIRED_FRAMES) {
        // console.log("[STEP 3] ✅ PASSED NOD DOWN");
        passStep();
      }

      return;
    }


    // ---------------------------------------------------------
    // STEP 4 — BLINK (FULLY REWRITTEN LIKE STEPS 0–2)
    // ---------------------------------------------------------
    if (step === 1) {
      // console.log("[STEP 4] Checking BLINK");

      if (!leftTop || !leftBottom || !rightTop || !rightBottom) return;

      // Vertical distances (eye openness)
      const leftOpen = Math.abs(leftTop.y - leftBottom.y);
      const rightOpen = Math.abs(rightTop.y - rightBottom.y);

      push(leftOpenHist.current, leftOpen, 10);
      push(rightOpenHist.current, rightOpen, 10);

      // Need enough smoothing buffer
      if (leftOpenHist.current.length < 6) return;

      const lMax = Math.max(...leftOpenHist.current);
      const lMin = Math.min(...leftOpenHist.current);

      const rMax = Math.max(...rightOpenHist.current);
      const rMin = Math.min(...rightOpenHist.current);

      // Reduction in openness during blink
      const leftDelta = lMax - lMin;
      const rightDelta = rMax - rMin;

      const REQUIRED_FRAMES = 2;

      // 1️⃣ Adaptive threshold (based on eye openness)
      const dynamicThreshold = Math.max(
        0.006,               // minimum for glasses users
        lMax * 0.28         // 28% closure from open state
      );

      // 2️⃣ Use average delta instead of strict both
      const avgDelta = (leftDelta + rightDelta) / 2;

      const blinkNow = avgDelta > dynamicThreshold;


      if (blinkNow) {
        counters.current.blink++;
        // console.log("[STEP 4] +1 blink_frame →", counters.current.blink);
      }
      else {
        counters.current.blink = 0;
        // console.log("[STEP 4] ❌ RESET — no blink");
      }

      if (counters.current.blink >= REQUIRED_FRAMES) {
        // console.log("[STEP 4] ✅ PASSED BLINK");
        passStep();
      }

      return;
    }

  }

  async function captureImage() {
    if (!canvasRef.current) return;
    if (!videoRef.current) return;

    detectionLockedRef.current = true; // 🔒 lock detection
    setIsCapturing(true);

    // Let UI update before heavy processing
    await new Promise(resolve => setTimeout(resolve, 30));

    const src = canvasRef.current;
    if (!src.width || !src.height) {
      setIsCapturing(false);
      return;
    }

    const out = document.createElement("canvas");

    // Slightly smaller for mobile performance boost
    const width = 400;   // was 500
    const height = 500;  // keep 4:5 ratio

    out.width = width;
    out.height = height;

    const ctx = out.getContext("2d");
    if (!ctx) {
      setIsCapturing(false);
      return;
    }

    const sx = (src.width - width) / 2;
    const sy = (src.height - height) / 2;

    ctx.drawImage(src, sx, sy, width, height, 0, 0, width, height);

    out.toBlob((blob) => {
      if (!blob) {
        setIsCapturing(false);
        return;
      }

      const file = new File([blob], "face.png", {
        type: "image/png",
      });

      setCapturedFile(file);

      const previewUrl = URL.createObjectURL(blob);
      setPreviewImage(previewUrl);
      setShowPreview(true);

      setIsCapturing(false);
    }, "image/png", 0.9);
  }

  function handleDownload() {
    if (!capturedFile) return;

    const url = URL.createObjectURL(capturedFile);
    const a = document.createElement("a");
    a.href = url;
    a.download = "selfie.png"; // file name
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }


  if (isCapturing) {
    stepText.current = "Please stay still";
  } else if (faceMissing) {
    stepText.current = "Face not detected";
  } else if (faceDistance === "close") {
    stepText.current = "Too Close!";
  } else if (faceDistance === "far") {
    stepText.current = "Move closer";
  } else if (!isFaceInside) {
    stepText.current = "Please come to center";
  } else if (currentStep >= stepCount) {
    stepText.current = "✅ Verification successful";
  } else {
    stepText.current = steps[currentStep];
  }


  async function resetAll() {
    // console.log("🔄 FULL RESET triggered");

    // Reset UI state
    setCurrentStep(0);
    setProgress(0);
    setCapturedFile(null);
    setShowPreview(false);
    setPreviewImage(null);
    setFaceMissing(false);

    currentStepRef.current = 0;
    isTransitioningRef.current = false;
    cooldownUntilRef.current = 0;
    faceLostCounter.current = 0;
    faceFoundCounter.current = 0;

    resetStats();

    // 🔥 Properly destroy old instances
    camRef.current?.stop();
    faceMeshRef.current?.close();

    camRef.current = null;
    faceMeshRef.current = null;

    // Small delay to allow DOM to stabilize
    setTimeout(() => {
      startCamera();
    }, 100);
  }

  const skipClickHandler = () => setSkipConfirmationModalClose(false)

  useEffect(() => {
    startCamera();

    return () => {
      camRef.current?.stop();
      faceMeshRef.current?.close();
    };
  }, []);


  return (
    <>
      <div style={{ padding: 10, textAlign: "center" }}>

        {!showPreview && (
          <>
            <Box sx={{
              marginBottom: '8px',
              '& .j-JDSAccordion-container': {
                marginLeft: '-10px'
              },
              '& .j-JDSAccordion-accordion-header-container': {
                maxWidth: 'fit-content',
                gap: '4px',
                alignItems: 'flex-start'
              },
              '& .j-JDSButton-container': {
                minHeight: 'unset',
                minWidth: 'unset',
                height: 'fit-content'
              },
              '& .j-JDSAccordion-children-container': {
                paddingTop: '6px',
                marginBottom: '8px'
              }
            }}>
              <Accordion>
                <AccordionPanel title="Selfie tips" showDivider={false}>
                  <Container direction="column">
                    <Text text="Ensure good lighting" appearance="body-xs" />
                    <Text text="Keep your face inside the circle" appearance="body-xs" />
                    <Text text="Keep the background clear, with no other faces." appearance="body-xs" />
                  </Container>
                </AccordionPanel>
              </Accordion>
              {/* <Text text="Selfie tips:" appearance="body-s-bold" /> */}
            </Box>
            <div style={{
              ...styles.circleWrapper,
              border: `4px solid ${!isFaceInside ? "#ff3b30" : '#13a10e'
                }`
            }}>
              <video ref={videoRef} autoPlay playsInline muted style={styles.video} />
              <canvas ref={canvasRef} style={styles.canvas} />
            </div>
          </>
        )}

        {showPreview && (
          <div style={{ marginTop: 20 }}>
            <img src={previewImage} alt="preview" style={styles.previewImg} />
          </div>
        )}

        {!showPreview && (
          <div style={{ marginTop: 20 }}>
            {/* <h3>{stepText}</h3> */}
            <Stack mb={1} alignItems={'center'}>
              <Text text={stepText.current} appearance="body-s-bold" />
            </Stack>
            {/* <div style={styles.progressOuter}>
            <div style={{ ...styles.progressInner, width: `${progress}%` }} />
          </div> */}
            <LinearProgress variant="determinate" value={progress} color={progress === 100 ? 'success' : progress > 75 ? 'warning' : 'info'} sx={{ height: '6px', borderRadius: '5px' }} />

          </div>
        )}

        <div style={styles.buttons}>
          {!showPreview && (
            <>
              <Button label="Capture" onClick={captureImage} disabled={currentStep < stepCount} loading={isCapturing}></Button>
              {!props?.skip ?
                <Button label="Close" kind="secondary" onClick={onClose} /> :
                <Button label="Skip" kind="secondary" onClick={skipClickHandler} />
              }
            </>
          )}

          {showPreview && (
            <Stack flexDirection={'row'} alignItems={'center'} gap={2}>
              <Button
                label="Confirm"
                onClick={() => {
                  if (capturedFile) {
                    onCapture(capturedFile);
                  }
                }}
                loading={isGetFaceCounting}>
              </Button>
              <Button label="Retake" kind="secondary"
                disabled={isGetFaceCounting}
                onClick={() => {
                  detectionLockedRef.current = false;
                  setShowPreview(false)
                  resetAll()
                }} />
              {/* <Box>
                <Icon ic={'IcDownload'} onClick={handleDownload} />
              </Box> */}
            </Stack>
          )}

        </div>
      </div>
      <JdsModal
        kind={"dialog"}
        header="Skip profile photo?"
        size="m"
        close={skipConfirmationModalClose}
        onClose={() => {
          setSkipConfirmationModalClose(true)
        }}
        primaryCTA={{
          title: "Upload now",
        }}
        secondaryCTA={{
          title: "Skip anyway",
        }}
        onPrimaryClick={() => setSkipConfirmationModalClose(true)}
        onSecondaryClick={() => {
          setSkipConfirmationModalClose(true);
          onClose();
          navigate(`/user-registered/photos?eventId=${loginDetails?.eventId}`)
        }}
      >
        <Container>
          <Text appearance="body-s" text="You won’t be able to see 'My Photos' until you upload a profile picture." />
        </Container>
      </JdsModal>
    </>
  );

}